import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { 
  ArrowLeft, 
  Users, 
  Clock, 
  CheckCircle, 
  XCircle,
  AlertCircle,
  Trophy,
  DollarSign,
  Target,
  Zap,
  Play
} from 'lucide-react';
import competitionReadinessService, { CompetitionReadiness, ParticipantReadiness } from '../../services/competitionReadinessService';
import { useAuth } from '../../contexts/AuthContext';

// Types
interface User {
  id: string;
  name: string;
  accountType: 'student' | 'public';
  gradeLevel?: number;
  university?: string;
  rating: number;
  wins: number;
  totalMatches: number;
  avatar?: string;
  profilePicture?: string;
  isOnline: boolean;
}

interface Competitor extends User {
  lastSeen?: string;
  currentStake?: number;
}

// Animations
const pulse = keyframes`
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.1); opacity: 0.7; }
  100% { transform: scale(1); opacity: 1; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const wave = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
`;

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  color: white;
  text-align: center;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 10px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.05);
  }
`;

const Title = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  margin: 0;
  color: #ffd700;
`;

const Description = styled.p`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.8);
  margin: 0;
  line-height: 1.5;
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 40px;
`;

const WaitingStatus = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 25px;
  padding: 40px;
  border: 2px solid rgba(255, 255, 255, 0.1);
  max-width: 600px;
  width: 100%;
`;

const StatusIcon = styled.div<{ isReady: boolean }>`
  font-size: 4rem;
  margin-bottom: 20px;
  color: ${props => props.isReady ? '#4caf50' : '#ff9800'};
  animation: ${props => props.isReady ? pulse : float} 2s infinite ease-in-out;
`;

const StatusTitle = styled.h3`
  font-size: 1.8rem;
  font-weight: 600;
  margin-bottom: 15px;
  color: white;
`;

const StatusDescription = styled.p`
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.5;
  margin-bottom: 25px;
`;

const PlayersSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;
  margin: 30px 0;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const PlayerCard = styled.div<{ isReady: boolean; isCurrentUser: boolean }>`
  background: ${props => props.isReady 
    ? 'rgba(76, 175, 80, 0.1)' 
    : 'rgba(255, 255, 255, 0.05)'
  };
  border: 2px solid ${props => props.isReady 
    ? '#4caf50' 
    : 'rgba(255, 255, 255, 0.2)'
  };
  border-radius: 20px;
  padding: 25px;
  text-align: center;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  ${props => props.isReady && css`
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: -100%;
      width: 100%;
      height: 100%;
      background: linear-gradient(90deg, transparent, rgba(76, 175, 80, 0.2), transparent);
      animation: ${wave} 2s infinite;
    }
  `}
`;

const PlayerAvatar = styled.div<{ isReady: boolean; isCurrentUser: boolean }>`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${props => {
    if (props.isReady) return 'linear-gradient(45deg, #4caf50, #8bc34a)';
    if (props.isCurrentUser) return 'linear-gradient(45deg, #2196f3, #03a9f4)';
    return 'linear-gradient(45deg, #9e9e9e, #757575)';
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 2rem;
  margin: 0 auto 15px;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 5px;
    right: 5px;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${props => props.isReady ? '#4caf50' : '#9e9e9e'};
    border: 3px solid white;
  }
`;

const PlayerName = styled.div`
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 8px;
  color: white;
`;

const PlayerDetails = styled.div`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 15px;
`;

const ReadyStatus = styled.div<{ isReady: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  color: ${props => props.isReady ? '#4caf50' : '#ff9800'};
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const CompetitionInfo = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 20px;
  padding: 25px;
  margin: 30px 0;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const InfoTitle = styled.h4`
  font-size: 1.2rem;
  font-weight: 600;
  color: #ffd700;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 20px;
`;

const InfoItem = styled.div`
  text-align: center;
`;

const InfoValue = styled.div`
  font-size: 1.3rem;
  font-weight: 700;
  color: white;
  margin-bottom: 5px;
`;

const InfoLabel = styled.div`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const ReadyButton = styled.button<{ isReady: boolean }>`
  background: ${props => props.isReady 
    ? 'linear-gradient(45deg, #f44336, #e91e63)' 
    : 'linear-gradient(45deg, #4caf50, #8bc34a)'
  };
  color: white;
  border: none;
  padding: 15px 30px;
  border-radius: 25px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 20px;
  
  &:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const CountdownSection = styled.div<{ isVisible: boolean }>`
  background: linear-gradient(135deg, #4caf50, #8bc34a);
  border-radius: 20px;
  padding: 30px;
  margin-top: 30px;
  text-align: center;
  transform: ${props => props.isVisible ? 'scale(1)' : 'scale(0.9)'};
  opacity: ${props => props.isVisible ? 1 : 0};
  transition: all 0.5s ease;
`;

const CountdownTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: white;
  margin-bottom: 15px;
`;

const CountdownTimer = styled.div`
  font-size: 3rem;
  font-weight: 700;
  color: white;
  margin-bottom: 15px;
`;

const CountdownMessage = styled.div`
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.9);
`;

const LoadingDots = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin: 20px 0;
`;

const Dot = styled.div<{ delay: number }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ffd700;
  animation: ${pulse} 1.5s infinite ease-in-out;
  animation-delay: ${props => props.delay}s;
`;

const ErrorMessage = styled.div`
  background: rgba(244, 67, 54, 0.1);
  border: 1px solid rgba(244, 67, 54, 0.3);
  color: #ff6b6b;
  padding: 10px 15px;
  border-radius: 10px;
  margin-top: 15px;
  font-size: 0.9rem;
  text-align: center;
`;

const WaitingRoom: React.FC<{
  competitor?: Competitor;
  roomId: string;
  competitionId: string;
  onBack: () => void;
  onCountdownComplete?: () => void;
}> = ({ competitor, roomId, competitionId, onBack, onCountdownComplete }) => {
  const { user: authUser } = useAuth();
  const [countdown, setCountdown] = useState<number>(5);
  const [showCountdown, setShowCountdown] = useState<boolean>(false);
  const [readiness, setReadiness] = useState<CompetitionReadiness | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get current user's readiness status
  const currentUserReadiness = readiness?.participants.find(
    p => p.userId === authUser?.id
  );
  
  const opponentReadiness = readiness?.participants.find(
    p => p.userId !== authUser?.id
  );

  const currentReady = currentUserReadiness?.isReady || false;
  const opponentReady = opponentReadiness?.isReady || false;

  // Initialize readiness tracking when component mounts
  useEffect(() => {
    const initializeReadiness = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const readinessData = await competitionReadinessService.initializeReadiness({
          competitionId,
          roomId,
          participantIds: [authUser?.id || '', competitor?.id || ''].filter(Boolean)
        });
        
        setReadiness(readinessData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to initialize readiness tracking');
        console.error('Error initializing readiness:', err);
      } finally {
        setLoading(false);
      }
    };

    if (authUser?.id && competitor?.id && roomId && competitionId) {
      initializeReadiness();
    }
  }, [authUser?.id, competitor?.id, roomId, competitionId]);

  // Start polling for readiness updates
  useEffect(() => {
    if (!roomId) return;

    const stopPolling = competitionReadinessService.startReadinessPolling(
      roomId,
      (readinessData) => {
        setReadiness(readinessData);
        
        // Handle countdown when both players are ready
        if (readinessData.status === 'ready' && !showCountdown) {
          setShowCountdown(true);
          const timer = setInterval(() => {
            setCountdown(prev => {
              if (prev <= 1) {
                clearInterval(timer);
                // Notify parent component that countdown is complete
                if (onCountdownComplete) {
                  onCountdownComplete();
                }
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
          
          return () => clearInterval(timer);
        }
      },
      2000 // Poll every 2 seconds
    );

    return stopPolling;
  }, [roomId, showCountdown, onCountdownComplete]);

  // Handle ready button click
  const handleReadyToggle = async () => {
    if (!roomId || loading) return;

    try {
      setLoading(true);
      setError(null);
      
      const newReadyState = !currentReady;
      const updatedReadiness = await competitionReadinessService.updateReadiness({
        roomId,
        isReady: newReadyState
      });
      
      setReadiness(updatedReadiness);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update readiness status');
      console.error('Error updating readiness:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusInfo = () => {
    if (currentReady && opponentReady) {
      return {
        icon: <CheckCircle />,
        title: 'Both Players Ready!',
        description: 'Competition will start in a few seconds...'
      };
    } else if (currentReady) {
      return {
        icon: <Clock />,
        title: 'Waiting for Opponent',
        description: 'You\'re ready! Waiting for your competitor to confirm readiness.'
      };
    } else {
      return {
        icon: <AlertCircle />,
        title: 'Confirm Your Readiness',
        description: 'Click the button below when you\'re ready to start the competition.'
      };
    }
  };

  const statusInfo = getStatusInfo();

  return (
    <Container>
      <Header>
        <BackButton onClick={onBack}>
          <ArrowLeft size={24} />
        </BackButton>
        <div>
          <Title>Waiting Room</Title>
          <Description>
            Confirm your readiness and wait for your opponent. 
            The competition will begin automatically when both players are ready.
          </Description>
        </div>
      </Header>

      <MainContent>
        <WaitingStatus>
          <StatusIcon isReady={currentReady && opponentReady}>
            {statusInfo.icon}
          </StatusIcon>
          <StatusTitle>{statusInfo.title}</StatusTitle>
          <StatusDescription>{statusInfo.description}</StatusDescription>
          
          {!currentReady && (
            <ReadyButton isReady={currentReady} onClick={handleReadyToggle} disabled={loading}>
              {loading ? (
                <>
                  <Clock size={20} style={{ marginRight: '8px' }} />
                  Updating...
                </>
              ) : (
                <>
                  <CheckCircle size={20} style={{ marginRight: '8px' }} />
                  I'm Ready
                </>
              )}
            </ReadyButton>
          )}
          
          {error && (
            <ErrorMessage>
              {error}
            </ErrorMessage>
          )}
          
          {currentReady && !opponentReady && (
            <LoadingDots>
              <Dot delay={0} />
              <Dot delay={0.2} />
              <Dot delay={0.4} />
            </LoadingDots>
          )}
        </WaitingStatus>

        <PlayersSection>
          <PlayerCard isReady={currentReady} isCurrentUser={true}>
            <PlayerAvatar isReady={currentReady} isCurrentUser={true}>
              Y
            </PlayerAvatar>
            <PlayerName>You</PlayerName>
            <PlayerDetails>
              Rating: 4.5 • Wins: 12
            </PlayerDetails>
            <ReadyStatus isReady={currentReady}>
              {currentReady ? (
                <>
                  <CheckCircle size={16} />
                  Ready
                </>
              ) : (
                <>
                  <XCircle size={16} />
                  Not Ready
                </>
              )}
            </ReadyStatus>
          </PlayerCard>

          <PlayerCard isReady={opponentReady} isCurrentUser={false}>
            <PlayerAvatar isReady={opponentReady} isCurrentUser={false}>
              {competitor?.name.charAt(0) || 'O'}
            </PlayerAvatar>
            <PlayerName>{competitor?.name || 'Opponent'}</PlayerName>
            <PlayerDetails>
              Rating: {competitor?.rating || 'N/A'} • Wins: {competitor?.wins || 'N/A'}
            </PlayerDetails>
            <ReadyStatus isReady={opponentReady}>
              {opponentReady ? (
                <>
                  <CheckCircle size={16} />
                  Ready
                </>
              ) : (
                <>
                  <Clock size={16} />
                  Waiting
                </>
              )}
            </ReadyStatus>
          </PlayerCard>
        </PlayersSection>

        <CompetitionInfo>
          <InfoTitle>
            <Trophy />
            Competition Details
          </InfoTitle>
          <InfoGrid>
            <InfoItem>
              <InfoValue>Science</InfoValue>
              <InfoLabel>Category</InfoLabel>
            </InfoItem>
            <InfoItem>
              <InfoValue>Text Input</InfoValue>
              <InfoLabel>Mode</InfoLabel>
            </InfoItem>
            <InfoItem>
              <InfoValue>KES 100</InfoValue>
              <InfoLabel>Stake</InfoLabel>
            </InfoItem>
            <InfoItem>
              <InfoValue>25</InfoValue>
              <InfoLabel>Questions</InfoLabel>
            </InfoItem>
          </InfoGrid>
        </CompetitionInfo>

        {showCountdown && (
          <CountdownSection isVisible={showCountdown}>
            <CountdownTitle>
              <Play size={24} style={{ marginRight: '10px' }} />
              Competition Starting!
            </CountdownTitle>
            <CountdownTimer>{countdown}</CountdownTimer>
            <CountdownMessage>
              Get ready to compete!
            </CountdownMessage>
          </CountdownSection>
        )}
      </MainContent>
    </Container>
  );
};

export default WaitingRoom;
